import moment from 'moment';
import { FinancialStatementPeriodFullWrite } from 'api/models/FinancialStatementPeriodFullWrite';
import { getArrayValue } from 'utilities';

export type NormalizableDate = Date | string | number | null | undefined;

/**
 * Normalizes values from APIs/localStorage into a valid Date instance (or null).
 * Accepts Date instances, ISO strings, or Unix timestamps.
 */
export const normalizeDate = (value: NormalizableDate): Date | null => {
  // Catches both null and undefined
  if (value == null) {
    return null;
  }
  
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Stable ISO key for Date/string inputs (useful for memoization keys).
 */
export const dateToISOStringKey = (value: NormalizableDate): string | null => {
  const d = normalizeDate(value);
  return d ? d.toISOString() : null;
};

export const isDateInPeriods = (
  date: string,
  periods: FinancialStatementPeriodFullWrite[],
  dateField: keyof FinancialStatementPeriodFullWrite,
  dateFormat: string
) => {
  const formattedDate = moment(date).format(dateFormat);
  const dates = getArrayValue(periods?.map(period => period?.[dateField]));
  return dates.includes(formattedDate);
};

export const isLastQuarter = (asOfDate: string, fyEnd: string) => {
  const date = moment(asOfDate, moment.ISO_8601, true);
  const fyEndMoment = moment(fyEnd, moment.ISO_8601, true);
  const fyEndStartMoment = fyEndMoment.clone().subtract(3, 'months');
  const dateIsWithinLastQuarter = date.isBetween(fyEndStartMoment, fyEndMoment, null, '[]');

  return dateIsWithinLastQuarter;
};

/**
 * deeply compares two date-like values (Date instance, ISO string, or null).
 * Returns true if they represent the same time.
 */
export const areDatesEqual = (a: NormalizableDate, b: NormalizableDate): boolean => {
  const dateA = normalizeDate(a);
  const dateB = normalizeDate(b);

  if (!dateA && !dateB) {
    return true;
  }
  if (!dateA || !dateB) {
    return false;
  }

  return dateA.getTime() === dateB.getTime();
};

/**
 * Add years (including fractional years) to a date.
 * Mirrors backend apps.core.utils.datetime.date_plus_years behavior.
 */
export const datePlusYears = ({
  startingDate,
  years,
}: {
  startingDate: moment.MomentInput;
  years: number | string | null | undefined;
}) => {
  const baseMoment = moment(startingDate).clone();
  if (!baseMoment.isValid() || years === null || years === undefined || years === '') {
    return null;
  }

  const yearsFloat = Number(years);
  if (Number.isNaN(yearsFloat)) {
    return null;
  }

  const integerYears = Math.trunc(yearsFloat);
  const fractionalPart = yearsFloat - integerYears;

  let totalDays = integerYears * 365;

  const startYear = baseMoment.year();
  const endYear = startYear + integerYears;

  for (let year = startYear; year < endYear; year += 1) {
    if (moment([year]).isLeapYear()) {
      totalDays += 1;
    }
  }

  if (fractionalPart > 0) {
    const yearForFractionalPart = startYear + integerYears;
    const dateAfterIntegerYears = baseMoment.clone().add(totalDays, 'days');

    let daysInYear = 365;
    if (moment([yearForFractionalPart]).isLeapYear()) {
      const feb29 = moment([yearForFractionalPart, 1, 29]);
      const fractionalDays365 = Math.trunc(fractionalPart * 365);
      const endDateWith365 = moment(dateAfterIntegerYears).add(fractionalDays365, 'days');
      daysInYear = endDateWith365.isSameOrAfter(feb29, 'day') ? 366 : 365;
    }

    const fractionalDays = Math.trunc(fractionalPart * daysInYear);
    totalDays += fractionalDays;
  }

  return baseMoment.clone().add(totalDays, 'days');
};
